import { APP_NAME, APP_TAGLINE, BRAND_LOGO, BRAND_SKY } from "@/lib/brand";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, MailWarning, Loader2, CheckCircle2 } from "lucide-react";

// Module-level guard: survives React's StrictMode double-mount so a single-use
// verification token is only ever submitted once per page session.
const verifyAttempted = new Set();

function Shell({ children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="verify-email-page">
      <div
        className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(37,99,235,0.88), rgba(59,130,246,0.42)), url(${BRAND_SKY})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 inline-flex items-center bg-white/95 rounded-xl px-3.5 py-2 shadow-sm w-fit">
          <img src={BRAND_LOGO} alt={APP_NAME} className="h-[31px] w-auto object-contain" />
        </div>
        <div className="relative z-10 fade-in-up">
          <p className="overline" style={{ color: "rgba(255,255,255,0.85)" }}>Verify your email</p>
          <h1 className="mt-4 text-4xl xl:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-md">{APP_TAGLINE}</h1>
        </div>
        <div className="relative z-10 text-xs text-white/70">© {new Date().getFullYear()} {APP_NAME} · {APP_TAGLINE}</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-6">
            <img src={BRAND_LOGO} alt={APP_NAME} className="h-8 w-auto object-contain" />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [state, setState] = useState("verifying"); // verifying | needs_workspace | error
  const [error, setError] = useState("");
  const [wsName, setWsName] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendEmail, setResendEmail] = useState("");

  useEffect(() => {
    if (!token) { setState("error"); setError("Missing verification link. Request a new one below."); return; }
    if (verifyAttempted.has(token)) return;
    verifyAttempted.add(token);
    api.post("/auth/verify-email", { token })
      .then(async ({ data }) => {
        // Cookie is now set — re-sync auth state so a stale /me from mount can't clobber it.
        await checkAuth();
        if (data.user?.workspace_setup_needed) {
          setState("needs_workspace");
        } else {
          toast.success("Email verified — welcome to Airava!");
          navigate("/", { replace: true });
        }
      })
      .catch((e) => {
        setState("error");
        setError(formatApiErrorDetail(e.response?.data?.detail) || "This verification link is invalid or has expired.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const nameWorkspace = async () => {
    if (wsName.trim().length < 2) { toast.error("Enter a workspace name"); return; }
    setBusy(true);
    try {
      await api.post("/auth/complete-workspace", { name: wsName.trim() });
      await checkAuth();
      toast.success("Workspace ready!");
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!/\S+@\S+\.\S+/.test(resendEmail)) { toast.error("Enter your account email"); return; }
    setBusy(true);
    try {
      await api.post("/auth/resend-verification", { email: resendEmail.trim() });
      toast.success("If your account is awaiting verification, a new link has been sent.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (state === "verifying") {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="verify-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Verifying your email…
        </div>
      </Shell>
    );
  }

  if (state === "needs_workspace") {
    return (
      <Shell>
        <div className="rounded-xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="verify-workspace-state">
          <div className="h-12 w-12 rounded-full bg-green-100 grid place-items-center">
            <CheckCircle2 className="h-6 w-6 text-green-600" />
          </div>
          <h1 className="mt-4 text-xl font-bold tracking-tight text-slate-900">Email verified</h1>
          <p className="mt-2 text-sm text-slate-500">Name your workspace to finish setting up.</p>
          <div className="mt-5 space-y-2">
            <Label className="text-xs font-medium text-slate-600">Workspace name</Label>
            <Input value={wsName} onChange={(e) => setWsName(e.target.value)} placeholder="e.g. Acme Inc." data-testid="verify-workspace-name"
              onKeyDown={(e) => e.key === "Enter" && nameWorkspace()} />
            <Button onClick={nameWorkspace} disabled={busy || wsName.trim().length < 2} className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] h-10" data-testid="verify-workspace-submit">
              {busy ? "Saving…" : "Enter workspace"}
              {!busy && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm" data-testid="verify-error-state">
        <div className="mx-auto h-12 w-12 rounded-full bg-amber-100 grid place-items-center">
          <MailWarning className="h-6 w-6 text-amber-600" />
        </div>
        <h1 className="mt-4 text-xl font-bold tracking-tight text-slate-900">Verification failed</h1>
        <p className="mt-2 text-sm text-slate-500" data-testid="verify-error">{error}</p>
        <div className="mt-6 space-y-2 text-left">
          <Label className="text-xs font-medium text-slate-600">Resend verification email</Label>
          <Input type="email" value={resendEmail} onChange={(e) => setResendEmail(e.target.value)} placeholder="you@company.com" data-testid="verify-resend-email" />
          <Button variant="outline" onClick={resend} disabled={busy} className="w-full" data-testid="verify-resend-btn">
            {busy ? "Sending…" : "Send new link"}
          </Button>
          <Button variant="ghost" onClick={() => navigate("/login")} className="w-full" data-testid="verify-to-login">Back to sign in</Button>
        </div>
      </div>
    </Shell>
  );
}
